import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/HeaderFooter/Footer';
import { useTheme } from '../../Context/themeContext';

const Products = () => {
  const { theme } = useTheme();
  const [isVisible, setIsVisible] = useState({});
  const navigate = useNavigate();

  // Theme-aware classes
  const isDark = theme === 'dark';
  
  const bgPrimary = isDark ? 'bg-[#0B1120]' : 'bg-gray-50';
  //const bgCard = isDark ? 'bg-[#0B1120]' : 'bg-gray-50';
  
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-blue-200/70' : 'text-gray-700';
  const textBlue = isDark ? 'text-blue-300' : 'text-blue-600';
  //const textBlueLight = isDark ? 'text-blue-200' : 'text-gray-600';
  //const textBlueLighter = isDark ? 'text-blue-400' : 'text-blue-600';
  
  //const borderColor = isDark ? 'border-blue-800/40' : 'border-gray-200';
  const shadowColor = isDark ? 'shadow-2xl' : 'shadow-xl';
  
  const buttonBg = isDark 
    ? 'bg-blue-600 hover:bg-blue-500'
    : 'bg-blue-500 hover:bg-blue-600';
  const buttonText = 'text-white';
  
  const overlayGradient = isDark 
    ? 'opacity-0 group-hover:opacity-20'
    : 'opacity-0 group-hover:opacity-10';

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observers = {};
    
    ['service1', 'service2', 'service3', 'service4'].forEach(id => {
      observers[id] = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              setIsVisible(prev => ({ ...prev, [id]: true }));
            }
          });
        },
        { threshold: 0.2 }
      );

      const element = document.getElementById(id);
      if (element) observers[id].observe(element);
    });

    return () => {
      Object.values(observers).forEach(observer => observer.disconnect());
    };
  }, []);

  const services = [
    {
      id: 1,
      title: "Biometric Hardware",
      shortDesc: "Latest biometric devices & installation",
      description: "High-quality biometric hardware devices including fingerprint scanners, facial recognition terminals, and hybrid systems. We supply, install, and maintain state-of-the-art attendance machines suitable for any business size. Cloud-connected devices with backup storage.",
      features: [
        "Fingerprint scanners",
        "Facial recognition terminals",
        "Hybrid biometric devices",
        "Professional installation",
        "Maintenance support",
        "Cloud synchronization"
      ],
      icon: (
        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
        </svg>
      ),
      image: "https://images.unsplash.com/photo-1581091226033-d5c48150dbaa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      color: "from-blue-500 to-blue-300",
      url: "/purchase-biometric-hardware"
    },
    {
      id: 2,
      title: "Biometric Attendance System",
      shortDesc: "Software solution for attendance tracking",
      description: "Eliminate time theft and manual tracking with our advanced biometric attendance software. Real-time tracking, instant reports, and seamless payroll integration for accurate workforce management. Works with various biometric hardware devices.",
      features: [
        "Fingerprint & facial recognition",
        "Real-time tracking",
        "GPS location tagging",
        "Mobile app access",
        "Payroll integration",
        "Customizable reports"
      ],
      icon: (
        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
        </svg>
      ),
      image: "https://images.unsplash.com/photo-1614064641938-3bbee52942c7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      color: "from-blue-600 to-blue-400"
    },
    {
      id: 3,
      title: "CRM Software",
      shortDesc: "Build stronger customer relationships",
      description: "Transform your customer relationships with our powerful CRM solution. Track interactions, manage leads, automate follow-ups, and analyze customer data to make informed decisions. Perfect for sales teams, marketing departments, and customer support professionals.",
      features: [
        "Contact management",
        "Lead tracking",
        "Sales pipeline management",
        "Email integration",
        "Customer analytics",
        "Task automation"
      ],
      icon: (
        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      image: "https://images.unsplash.com/photo-1552581234-26160f608093?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      color: "from-blue-500 to-blue-300"
    },
    {
      id: 4,
      title: "Employee Management Software",
      shortDesc: "Complete HR solution for modern businesses",
      description: "Streamline your HR operations with our comprehensive employee management system. Track attendance, manage leaves, process payroll, and handle performance reviews all in one place. Our software automates routine tasks, reduces paperwork, and provides real-time insights into your workforce.",
      features: [
        "Employee database management",
        "Leave & attendance tracking",
        "Performance appraisal system",
        "Payroll integration",
        "Document management",
        "Employee self-service portal"
      ],
      icon: (
        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      color: "from-blue-600 to-blue-400"
    },
  ];

  function payhandler(service) {
    console.log("Service Solution:", service);
    if (service.id === 1) navigate('/purchase-biometric/hardware');
    if (service.id === 2) navigate('/purchase-software/plan');
  }

  return (
    <div className={`min-h-screen ${bgPrimary} overflow-hidden transition-colors duration-300`}>
      {/* Services Grid */}
      <div className="px-4 py-24 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="space-y-16">
          {services.map((service, index) => (
            <div
              key={service.id}
              id={`service${service.id}`}
              className={`transition-all duration-1000 transform ${
                isVisible[`service${service.id}`] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-12 items-center`}>
                {/* Image Section with Animation */}
                <div className="w-full lg:w-1/2 group">
                  <div className={`relative overflow-hidden ${shadowColor} rounded-2xl`}>
                    {/* Shine effect */}
                    <div className="absolute inset-0 transition-transform duration-1000 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover:translate-x-full"></div>
                    
                    {/* Image */}
                    <img
                      src={service.image}
                      alt={service.title}
                      className="object-cover w-full h-64 transition-transform duration-700 transform lg:h-80 group-hover:scale-110"
                    />
                    
                    {/* Overlay gradient */}
                    <div className={`absolute inset-0 bg-gradient-to-t ${service.color} ${overlayGradient} transition-opacity duration-500`}></div>
                  </div>
                </div>

                {/* Content Section with Animation */}
                <div className="w-full space-y-6 lg:w-1/2">
                  {/* Icon with pulse animation */}
                  <div className={`${isDark ? 'text-blue-400' : 'text-blue-600'} animate-pulse`}>
                    {service.icon}
                  </div>

                  {/* Title */}
                  <h2 className={`text-3xl font-bold ${textPrimary}`}>
                    {service.title}
                  </h2>

                  {/* Short description */}
                  <p className={`text-lg font-medium ${textBlue}`}>
                    {service.shortDesc}
                  </p>

                  {/* Full description */}
                  <p className={`leading-relaxed ${textSecondary}`}>
                    {service.description}
                  </p>

                  {/* Features Grid */}
                  <div className="grid grid-cols-1 gap-3 pt-4 sm:grid-cols-2">
                    {service.features.map((feature, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center space-x-2 ${isDark ? 'text-blue-200' : 'text-gray-700'} transition-transform duration-300 transform hover:translate-x-1`}
                      >
                        <svg className={`flex-shrink-0 w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  {service.id === 1 && (
                    <div className="pt-4">
                      <button className={`relative inline-flex items-center justify-center px-6 py-3 overflow-hidden font-medium ${buttonText} transition-all duration-300 transform ${buttonBg} rounded-lg hover:scale-105`}>
                        <span onClick={() => payhandler(service)} className="relative">Purchase Now</span>
                        <svg className={`w-5 h-5 ml-2 transition-transform group-hover:translate-x-1 ${isDark ? 'text-white' : 'text-white'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </button>
                    </div>
                  )}
                  {service.id === 2 && (
                    <div className="pt-4">
                      <button className={`relative inline-flex items-center justify-center px-6 py-3 overflow-hidden font-medium ${buttonText} transition-all duration-300 transform ${buttonBg} rounded-lg hover:scale-105`}>
                        {/* <span onClick={() => payhandler(service)} className="relative">Download Now</span>
                        <svg className={`w-5 h-5 ml-2 transition-transform group-hover:translate-x-1 ${isDark ? 'text-white' : 'text-white'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg> */}
                        <a
                          href="/downloads/app-arm64-v8a-release 35.apk"
                          target="_blank"
                          rel="noopener noreferrer"
                          download="CICO-Time.apk"
                          className="relative"
                          >
                          <span>Download CICO-Time App</span>
                        </a>
                        <svg className={`w-5 h-5 ml-2 transition-transform group-hover:translate-x-1 -rotate-90 ${isDark ? 'text-white' : 'text-white'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* CTA Section */}
      <Footer />
    </div>
  );
};

export default Products;
